import $ from "jquery";
import { addFilter } from "@wordpress/hooks";
import Cookies from "./cookies";

/**
 * Breathing room left below the fixed header when the page is scrolled to a
 * linked element, so it does not sit flush against it.
 */
const FIXED_HEADER_SCROLL_GAP = 16;

function escapeHtml(str) {
  return String(str ?? "").replace(/[&<>"']/g, (ch) => {
    return {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[ch];
  });
}

class Theme {
  constructor() {
    this.menuToggleLink = document.querySelector(
      ".site-header__menu-toggle-link",
    );
    this.siteHeader = document.querySelector(".site-header");
    this.searchToggle = document.querySelector(".site-header__search-toggle");
    this.searchWrapper = document.querySelector(".search-wrapper");
    this.searchClose = document.querySelector(".search-wrapper__close-button");

    this.defaultSize = 16;
    this.accessibleFont = false;

    // bind handlers used as event callbacks
    this.equalizeSite = this.equalizeSite.bind(this);
    this.adjustBodyPaddingForAdminBar =
      this.adjustBodyPaddingForAdminBar.bind(this);
    this.adjustSiteMainPaddingForLogoBar =
      this.adjustSiteMainPaddingForLogoBar.bind(this);

    this.init();
  }

  init() {
    this.initMenuToggle();
    this.initSearch();
    this.initEqualizeSite();
    this.initAdminBarAdjustment();
    this.initMegaMenu();
    this.initLogoBarAdjustment();
    this.initContentCards();
    this.initOurPartnersMarquee();
    this.initHomeBannerVideo();
    this.initResourcesBlock();
    this.initQuotesSlider();
    this.initMembersBlockRoleToggle();
    this.initMembersBlockEmailModal();
    this.initSearchPage();
    this.initLocationSearchAutocomplete();
    this.initPostShare();
    this.initFixedHeaderScrollOffset();
    this.initAccessibility();
  }

  /**
   * Toggles home banner video playback and swaps the pause/play icon.
   */
  initHomeBannerVideo() {
    const banners = document.querySelectorAll(".home-banner");

    if (!banners.length) return;

    banners.forEach((banner) => {
      const video = banner.querySelector("video");
      const button = banner.querySelector(".home-banner__media-pause");

      if (!video || !button) return;

      const setPlaying = (isPlaying) => {
        button.setAttribute(
          "aria-label",
          isPlaying ? "Pause video" : "Play video",
        );
        button.innerHTML = isPlaying
          ? "<i class='icon icon-pause' aria-hidden='true'></i>"
          : "<i class='icon icon-play' aria-hidden='true'></i>";
      };

      button.addEventListener("click", () => {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });

      video.addEventListener("play", () => setPlaying(true));
      video.addEventListener("pause", () => setPlaying(false));

      setPlaying(!video.paused);
    });
  }

  /**
   * Expands the share network icons on the single post templates when the
   * custom share button is clicked.
   */
  initPostShare() {
    const shareWidgets = document.querySelectorAll("[data-post-share]");

    if (!shareWidgets.length) return;

    shareWidgets.forEach((widget) => {
      const toggle = widget.querySelector(".post-share__toggle");

      if (!toggle) return;

      const setOpen = (isOpen) => {
        widget.classList.toggle("is-open", isOpen);
        toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      };

      toggle.addEventListener("click", (e) => {
        e.preventDefault();
        setOpen(!widget.classList.contains("is-open"));
      });

      document.addEventListener("click", (e) => {
        if (!widget.contains(e.target)) setOpen(false);
      });

      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape" || !widget.classList.contains("is-open")) return;
        setOpen(false);
        toggle.focus();
      });
    });
  }

  /**
   * Adds a Long Term Care Home autocomplete to the Locations Map search field.
   * Selecting a suggestion drops that home's address into the search box and
   * runs the map search from there.
   */
  initLocationSearchAutocomplete() {
    const inputs = document.querySelectorAll(
      '.wp-block-tribe-blocks-locations-map__search-section input[name="search"]',
    );

    if (
      !inputs.length ||
      typeof window.oarcTheme === "undefined" ||
      !window.oarcTheme.ajaxUrl
    ) {
      return;
    }

    inputs.forEach((input) => this.setupLocationAutocomplete(input));
  }

  setupLocationAutocomplete(input) {
    const section = input.closest(
      ".wp-block-tribe-blocks-locations-map__search-section",
    );
    const form = input.closest("form");

    if (!section || !form) {
      return;
    }

    section.classList.add("has-rh-autocomplete");
    input.setAttribute("autocomplete", "off");

    const list = document.createElement("ul");
    list.className = "rh-autocomplete";
    list.setAttribute("role", "listbox");
    list.hidden = true;
    section.appendChild(list);

    let items = [];
    let activeIndex = -1;
    let debounceTimer = null;
    let lastQuery = "";
    let controller = null;

    const closeList = () => {
      list.hidden = true;
      list.innerHTML = "";
      items = [];
      activeIndex = -1;
    };

    const render = () => {
      if (!items.length) {
        closeList();
        return;
      }

      list.innerHTML = items
        .map(
          (item, i) =>
            `<li class="rh-autocomplete__item${
              i === activeIndex ? " is-active" : ""
            }" role="option" data-index="${i}">` +
            `<span class="rh-autocomplete__name">${escapeHtml(
              item.label,
            )}</span>` +
            `<span class="rh-autocomplete__address">${escapeHtml(
              item.value,
            )}</span>` +
            `</li>`,
        )
        .join("");
      list.hidden = false;
    };

    const choose = (item) => {
      if (!item) {
        return;
      }
      input.value = item.value;
      closeList();
      // Trigger the plugin's jQuery submit handler; a native form.submit()
      // would bypass the delegated listener that runs the map search.
      $(form).trigger("submit");
    };

    const fetchItems = async (term) => {
      if (controller) {
        controller.abort();
      }
      controller = new AbortController();

      try {
        const response = await fetch(window.oarcTheme.ajaxUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: new URLSearchParams({
            action: "oarc_long_term_care_home_search",
            nonce: window.oarcTheme.longTermCareHomeNonce || "",
            search: term,
          }),
          signal: controller.signal,
        });

        const payload = await response.json();

        if (!payload || !payload.success) {
          closeList();
          return;
        }

        items = (payload.data && payload.data.items) || [];
        activeIndex = -1;
        render();
      } catch (error) {
        if (error.name !== "AbortError") {
          closeList();
        }
      }
    };

    input.addEventListener("input", () => {
      const term = input.value.trim();

      if (term.length < 2) {
        closeList();
        lastQuery = "";
        return;
      }

      if (term === lastQuery) {
        return;
      }

      lastQuery = term;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchItems(term), 200);
    });

    input.addEventListener("keydown", (e) => {
      if (list.hidden || !items.length) {
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        activeIndex = (activeIndex + 1) % items.length;
        render();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        activeIndex = (activeIndex - 1 + items.length) % items.length;
        render();
      } else if (e.key === "Enter") {
        if (activeIndex >= 0) {
          e.preventDefault();
          choose(items[activeIndex]);
        }
      } else if (e.key === "Escape") {
        closeList();
      }
    });

    // mousedown (not click) so selection registers before the input blurs.
    list.addEventListener("mousedown", (e) => {
      const li = e.target.closest(".rh-autocomplete__item");
      if (!li) {
        return;
      }
      e.preventDefault();
      choose(items[parseInt(li.getAttribute("data-index"), 10)]);
    });

    document.addEventListener("click", (e) => {
      if (!section.contains(e.target)) {
        closeList();
      }
    });
  }

  initSelect2() {
    if (typeof $.fn.select2 === "undefined") return;

    $(".resources-block__form select, .search-page__form select").each(
      function initResourceSelect() {
        const $select = $(this);
        if ($select.data("select2")) return;

        const $dropdownParent = $select.closest(
          ".resources-block__filter, .search-page__filter",
        );
        if (!$dropdownParent.length) return;

        $select.select2({
          width: "100%",
          minimumResultsForSearch: 10,
          dropdownParent: $dropdownParent,
        });

        const dropdown = $select.data("select2")?.dropdown;
        if (!dropdown?._positionDropdown) return;

        if (dropdown._attachPositioningHandler) {
          const attachPositioningHandler =
            dropdown._attachPositioningHandler.bind(dropdown);
          dropdown._attachPositioningHandler =
            function attachWithoutScrollLock(container) {
              attachPositioningHandler(container);

              // Select2 pins scrollTop on every scrollable ancestor while the
              // dropdown is open. The theme makes <body> the scroll container,
              // so that freezes the whole page. Drop those handlers; the
              // reposition handler Select2 binds to window is untouched.
              this.$container.parents().off("scroll.select2");
            };
        }

        const positionDropdown = dropdown._positionDropdown.bind(dropdown);
        dropdown._positionDropdown = function forceDropdownBelow() {
          positionDropdown();

          const { $container, $dropdown, $dropdownContainer, $dropdownParent } =
            this;
          const containerRect = $container[0].getBoundingClientRect();
          const parentRect = $dropdownParent[0].getBoundingClientRect();

          $dropdown
            .removeClass("select2-dropdown--above")
            .addClass("select2-dropdown--below");
          $container
            .removeClass("select2-container--above")
            .addClass("select2-container--below");

          $dropdownContainer.css({
            top:
              containerRect.bottom -
              parentRect.top +
              $dropdownParent.scrollTop(),
            left:
              containerRect.left -
              parentRect.left +
              $dropdownParent.scrollLeft(),
            zIndex: 2,
          });
        };
      },
    );
  }

  initMenuToggle() {
    if (!this.menuToggleLink || !this.siteHeader) return;

    const menuToggleLabel = this.menuToggleLink.querySelector("span");
    if (!menuToggleLabel) return;

    const menuToggleOpenText = menuToggleLabel.textContent;
    const menuToggleCloseText = menuToggleLabel.dataset.closeText || "";

    const setMenuToggleOpen = (isOpen) => {
      menuToggleLabel.textContent = isOpen
        ? menuToggleCloseText
        : menuToggleOpenText;
      this.menuToggleLink.setAttribute(
        "aria-expanded",
        isOpen ? "true" : "false",
      );
      this.menuToggleLink.setAttribute(
        "aria-label",
        isOpen ? "Close menu" : "Open menu",
      );
    };

    this.menuToggleLink.addEventListener("click", (event) => {
      event.preventDefault();
      const isOpen = this.siteHeader.classList.toggle("expanded-menu");
      setMenuToggleOpen(isOpen);
    });

    document.addEventListener("keydown", (event) => {
      if (
        event.key === "Escape" &&
        this.siteHeader.classList.contains("expanded-menu")
      ) {
        this.siteHeader.classList.remove("expanded-menu");
        setMenuToggleOpen(false);
        this.menuToggleLink.focus();
      }
    });

    setMenuToggleOpen(this.siteHeader.classList.contains("expanded-menu"));
  }

  initSearch() {
    if (this.searchToggle && this.searchWrapper) {
      this.searchToggle.addEventListener("click", (e) => {
        e.preventDefault();
        this.searchWrapper.classList.toggle("active");
        this.searchToggle.focus();
      });
    }

    if (this.searchClose && this.searchWrapper) {
      this.searchClose.addEventListener("click", (e) => {
        e.preventDefault();
        this.searchWrapper.classList.remove("active");
      });
    }
  }

  equalizeSite() {
    if (this.accessibleFont) return;

    // Breakpoints and design reference sizes
    const $bptablet = 768;
    const $bpdesktop = 768;
    const $mobile = 390;
    const $tablet = 768;
    const $desktop = 1440;
    const $fsmobile = 16;
    const $fstablet = 16;
    const $fsdesktop = 16;

    const $ww = window.innerWidth;
    let $fontsize = 16;

    if ($ww < $bpdesktop) {
      $fontsize = 16;
    } else if ($ww > 400 && $ww <= 768) {
      $fontsize = 16;
    } else if ($ww > 939 && $ww <= 1072) {
      $fontsize = 14;
    } else if ($ww <= 1400) {
      $fontsize = 14;
    } else if ($ww <= 1560) {
      $fontsize = $fsdesktop * ($ww / $desktop);
    } else {
      $fontsize = 16;
    }

    $fontsize = parseInt($fontsize, 10) + this.getFontSizeAdjustment();

    document.documentElement.style.fontSize = $fontsize + "px";
    this.defaultSize = $fontsize;
  }

  initEqualizeSite() {
    window.addEventListener("load", this.equalizeSite);
    window.addEventListener("resize", this.equalizeSite);
    this.equalizeSite();
  }

  /**
   * Font size and contrast controls in the header accessibility menu.
   * Preferences are stored in cookies so they persist across pages.
   */
  initAccessibility() {
    this.applyContrastFromCookie();

    document.addEventListener("click", (e) => {
      const accessibility = document.querySelector(".header__accessibility");
      const accessibilityLink = e.target.closest(".accessibility-link");
      const accessibilityItem = accessibility?.querySelector(":scope > .menu > li");

      if (accessibilityLink) {
        e.preventDefault();
        const isOpen = accessibilityItem?.classList.toggle("is-open");
        accessibilityLink.setAttribute(
          "aria-expanded",
          isOpen ? "true" : "false",
        );
        return;
      }

      if (accessibility && !accessibility.contains(e.target)) {
        accessibilityItem?.classList.remove("is-open");
        accessibility
          .querySelector(".accessibility-link")
          ?.setAttribute("aria-expanded", "false");
      }

      const smallerFont = e.target.closest(".smaller-font");
      if (smallerFont) {
        e.preventDefault();
        this.changeFontSize(-1);
        return;
      }

      const largerFont = e.target.closest(".larger-font");
      if (largerFont) {
        e.preventDefault();
        this.changeFontSize(1);
        return;
      }

      const resetFont = e.target.closest(".reset-font");
      if (resetFont) {
        e.preventDefault();
        this.setCookie("fontsize", 0);
        this.equalizeSite();
        return;
      }

      const highContrast = e.target.closest(".high-contrast-btn");
      if (highContrast) {
        e.preventDefault();
        this.setContrast("high");
        return;
      }

      const lowContrast = e.target.closest(".low-contrast-btn");
      if (lowContrast) {
        e.preventDefault();
        this.setContrast("low");
        return;
      }

      const resetContrast = e.target.closest(".reset-contrast-btn");
      if (resetContrast) {
        e.preventDefault();
        this.setContrast(0);
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;

      const accessibility = document.querySelector(".header__accessibility");
      const accessibilityItem = accessibility?.querySelector(
        ":scope > .menu > li.is-open",
      );
      if (!accessibilityItem) return;

      accessibilityItem.classList.remove("is-open");
      const link = accessibility.querySelector(".accessibility-link");
      link?.setAttribute("aria-expanded", "false");
      link?.focus();
    });
  }

  getCookie(name) {
    return Cookies.get(name);
  }

  setCookie(name, value) {
    Cookies.set(name, value);
  }

  getFontSizeAdjustment() {
    const fontsize = this.getCookie("fontsize");
    if (typeof fontsize === "undefined") return 0;

    const parsed = parseInt(fontsize, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  changeFontSize(delta) {
    this.setCookie("fontsize", this.getFontSizeAdjustment() + delta);
    this.equalizeSite();
  }

  setContrast(value) {
    document.body.classList.remove("low-contrast", "high-contrast");

    if (value === "high" || value === "low") {
      document.body.classList.add(`${value}-contrast`);
    }

    this.setCookie("contrast", value);
  }

  applyContrastFromCookie() {
    const contrast = this.getCookie("contrast");

    if (contrast === "high" || contrast === "low") {
      document.body.classList.remove("low-contrast", "high-contrast");
      document.body.classList.add(`${contrast}-contrast`);
    }
  }

  adjustBodyPaddingForAdminBar() {
    const adminBar = document.getElementById("wpadminbar");
    const header = document.querySelector("header");

    if (!header) return;

    if (adminBar && adminBar.offsetHeight > 0) {
      const adminBarHeight = adminBar.offsetHeight;
      header.style.paddingTop = adminBarHeight + "px";
      if (this.searchWrapper)
        this.searchWrapper.style.top = adminBarHeight + "px";
    } else {
      header.style.paddingTop = "0px";
      if (this.searchWrapper) this.searchWrapper.style.top = "0px";
    }
  }

  initAdminBarAdjustment() {
    window.addEventListener("load", this.adjustBodyPaddingForAdminBar);
    window.addEventListener("resize", this.adjustBodyPaddingForAdminBar);
    this.adjustBodyPaddingForAdminBar();
  }

  /**
   * Measures how much of the top of the viewport is covered by the elements
   * pinned to it: the site header, and the admin bar for logged in users.
   *
   * Only what is actually pinned counts, so the admin bar is left out on the
   * small screens where it scrolls away with the page. The lowest edge is used
   * rather than a total, because adjustBodyPaddingForAdminBar() pads the header
   * to sit below the admin bar, meaning the header already covers both.
   *
   * @returns {number} The covered height in pixels.
   */
  getFixedHeaderOffset() {
    const elements = [this.siteHeader, document.getElementById("wpadminbar")];

    return elements.reduce((offset, element) => {
      if (!element) return offset;

      const styles = window.getComputedStyle(element);

      if (styles.position !== "fixed" && styles.position !== "sticky") {
        return offset;
      }

      if (styles.display === "none" || styles.visibility === "hidden") {
        return offset;
      }

      const rect = element.getBoundingClientRect();

      // A sticky element that has not been scrolled to yet is not covering
      // anything, so it is ignored until it reaches the top of the viewport.
      if (rect.height === 0 || rect.top > window.innerHeight / 2) {
        return offset;
      }

      return Math.max(offset, rect.bottom);
    }, 0);
  }

  /**
   * The site header is fixed to the top of the viewport, so anything the page
   * scrolls to has to clear it. Blocks that scroll on their own ask for that
   * distance through a filter rather than measuring the header themselves.
   */
  initFixedHeaderScrollOffset() {
    addFilter(
      "tribeBlocks.accordion.scrollOffset",
      "oarc/fixed-header-offset",
      (offset) => {
        const headerOffset = this.getFixedHeaderOffset();

        if (!headerOffset) return offset;

        // Measured at the moment of the scroll, so an offset already asked for
        // in CSS is respected when it is the larger of the two.
        return Math.max(offset, headerOffset + FIXED_HEADER_SCROLL_GAP);
      },
    );
  }

  initMegaMenu() {
    const megaMenu = document.querySelector(".mega-menu-bar__mega-menu");
    if (!megaMenu) return;

    const clearActive = () => {
      const activeItems = megaMenu.querySelectorAll(
        "li.menu-item-has-children.active",
      );
      activeItems.forEach((li) => li.classList.remove("active"));
      // ensure any body-level menu open state is cleared as well
      if (document && document.body)
        document.body.classList.remove("menu-opened");

      megaMenu
        .querySelectorAll(".mega-menu-bar__link-description-content.is-visible")
        .forEach((content) => {
          const target = content.querySelector(".link-description");
          if (target) target.innerHTML = "";
          content.classList.remove("is-visible");
        });
    };

    megaMenu.addEventListener("click", function (e) {
      if (
        e.target.closest &&
        e.target.closest(".mega-menu-bar__close-mega-menu")
      ) {
        e.preventDefault();
        clearActive();
        return;
      }

      const anchor = e.target.closest("a");
      if (!anchor) return;

      const parentLi = anchor.parentElement;
      if (!parentLi || !parentLi.classList.contains("menu-item-has-children"))
        return;

      e.stopPropagation();

      const href = anchor.getAttribute("href");
      if (!href || href === "#" || href.startsWith("#")) {
        e.preventDefault();
      }

      const isActive = parentLi.classList.contains("active");
      clearActive();
      if (!isActive) {
        parentLi.classList.add("active");
        document.body.classList.add("menu-opened");
      }
    });

    document.addEventListener("click", function (e) {
      if (!megaMenu.contains(e.target)) clearActive();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") clearActive();
    });

    const ITEM_SELECTOR = ".menu-item:not(.menu-label)";

    function getDescriptionContent(menuItem) {
      const wrapper = menuItem.closest(".mega-menu-bar__menu-wrapper");
      if (!wrapper) return null;
      return wrapper.querySelector(".mega-menu-bar__link-description-content");
    }

    function showDescription(content, description) {
      if (!content) return;
      const target = content.querySelector(".link-description");
      if (!target) return;
      target.innerHTML = description;
      content.classList.add("is-visible");
    }

    function hideDescription(content) {
      if (!content) return;
      const target = content.querySelector(".link-description");
      if (target) target.innerHTML = "";
      content.classList.remove("is-visible");
    }

    megaMenu.addEventListener("mouseover", function (e) {
      const menuItem = e.target.closest(ITEM_SELECTOR);
      if (!menuItem || !megaMenu.contains(menuItem)) return;

      const content = getDescriptionContent(menuItem);
      if (!content) return;

      const anchor = menuItem.querySelector(
        ":scope > a[data-link-description]",
      );
      const description = anchor
        ? anchor.getAttribute("data-link-description")
        : "";

      if (description) {
        showDescription(content, description);
      } else {
        hideDescription(content);
      }
    });

    megaMenu.addEventListener("mouseout", function (e) {
      const menuItem = e.target.closest(ITEM_SELECTOR);
      if (!menuItem || !megaMenu.contains(menuItem)) return;

      const related = e.relatedTarget;
      if (related && menuItem.contains(related)) return;

      // Keep description if moving to another item in the same submenu.
      if (related && related.closest) {
        const nextItem = related.closest(ITEM_SELECTOR);
        if (
          nextItem &&
          megaMenu.contains(nextItem) &&
          nextItem.closest(".mega-menu-bar__menu-wrapper") ===
            menuItem.closest(".mega-menu-bar__menu-wrapper")
        ) {
          return;
        }
      }

      hideDescription(getDescriptionContent(menuItem));
    });
  }

  adjustSiteMainPaddingForLogoBar() {
    const logoBar = document.querySelector(".site-header__logo-bar");
    const siteMain = document.querySelector(".site-main");

    if (siteMain && logoBar) {
      const logoBarHeight = logoBar.offsetHeight;
      siteMain.style.paddingTop = logoBarHeight + "px";
    }
  }

  initLogoBarAdjustment() {
    window.addEventListener("load", this.adjustSiteMainPaddingForLogoBar);
    window.addEventListener("resize", this.adjustSiteMainPaddingForLogoBar);
    this.adjustSiteMainPaddingForLogoBar();
  }

  initOurPartnersMarquee() {
    const sections = document.querySelectorAll(".our-partners");
    if (!sections.length) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    sections.forEach((section) => {
      const gallery = section.querySelector(".wp-block-gallery");
      if (!gallery || gallery.dataset.marqueeInit === "true") return;

      const items = gallery.querySelectorAll(
        ":scope > figure, :scope > .blocks-gallery-item",
      );
      if (!items.length) return;

      gallery.dataset.marqueeInit = "true";

      const marquee = document.createElement("div");
      marquee.className = "our-partners-marquee";

      const viewport = document.createElement("div");
      viewport.className = "our-partners-marquee__viewport";

      const track = document.createElement("div");
      track.className = "our-partners-marquee__track";

      const groupPrimary = document.createElement("div");
      groupPrimary.className = "our-partners-marquee__group";

      const groupDuplicate = document.createElement("div");
      groupDuplicate.className = "our-partners-marquee__group";
      groupDuplicate.setAttribute("aria-hidden", "true");

      items.forEach((item) => {
        groupPrimary.appendChild(item);
      });

      Array.from(groupPrimary.children).forEach((item) => {
        groupDuplicate.appendChild(item.cloneNode(true));
      });

      track.appendChild(groupPrimary);
      track.appendChild(groupDuplicate);
      viewport.appendChild(track);
      marquee.appendChild(viewport);

      let toggle = section.querySelector(".our-partners-marquee__toggle");
      if (!toggle) {
        toggle = document.createElement("button");
        toggle.type = "button";
        toggle.className = "our-partners-marquee__toggle";
      }
      marquee.appendChild(toggle);

      gallery.replaceWith(marquee);
      section.classList.add("our-partners--marquee-ready");

      let paused = reducedMotion.matches;
      if (paused) marquee.classList.add("is-reduced-motion");

      const setPaused = (isPaused) => {
        paused = isPaused;
        marquee.classList.toggle("is-paused", isPaused);
        toggle.setAttribute("aria-pressed", isPaused ? "true" : "false");
        toggle.innerHTML = isPaused
          ? "<i class='icon icon-play'></i>"
          : "<i class='icon icon-pause'></i>";
      };

      const setDuration = () => {
        const width = groupPrimary.getBoundingClientRect().width;
        if (!width) return;
        const pixelsPerSecond = 60;
        const duration = Math.max(width / pixelsPerSecond, 12);
        track.style.setProperty("--marquee-duration", `${duration}s`);
      };

      toggle.addEventListener("click", () => {
        setPaused(!paused);
      });

      reducedMotion.addEventListener("change", (event) => {
        if (event.matches) {
          marquee.classList.add("is-reduced-motion");
          setPaused(true);
        } else {
          marquee.classList.remove("is-reduced-motion");
        }
      });

      setPaused(paused);
      setDuration();

      window.addEventListener("load", setDuration, { once: true });

      if (typeof ResizeObserver !== "undefined") {
        const resizeObserver = new ResizeObserver(setDuration);
        resizeObserver.observe(groupPrimary);
      }

      groupPrimary.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", setDuration, { once: true });
        }
      });
    });
  }

  initContentCards() {
    document.addEventListener("click", (e) => {
      const showBtn = e.target.closest(".content-cards__show-details");
      if (showBtn) {
        e.preventDefault();
        const card = showBtn.closest(".content-cards__card");
        if (card) {
          document
            .querySelectorAll(".content-cards__card.expanded")
            .forEach((el) => el.classList.remove("expanded"));
          card.classList.add("expanded");
        }
        return;
      }

      const hideBtn = e.target.closest(".content-cards__hide-details");
      if (hideBtn) {
        e.preventDefault();
        const card = hideBtn.closest(".content-cards__card");
        if (card) card.classList.remove("expanded");
      }
    });
  }

  initMembersBlockRoleToggle() {
    const updateMembersBlockStacking = (block) => {
      const items = block.querySelectorAll(".members-block__item");

      items.forEach((item, index) => {
        const isActive = item.querySelector(".members-block__role.active");
        item.style.zIndex = isActive ? String(1000 - index) : "";
      });
    };

    document.addEventListener("click", (e) => {
      const toggle = e.target.closest(".members-block__toggle-role");
      if (!toggle) return;

      e.preventDefault();

      const role = toggle.closest(".members-block__role");
      const block = toggle.closest(".members-block");
      if (!role || !block) return;

      role.classList.toggle("active");
      updateMembersBlockStacking(block);
    });
  }

  initMembersBlockEmailModal() {
    const formId = window.oarcTheme?.membersEmailFormId || 3;
    let lastFocusedElement = null;

    // Resolve the modal fresh each time rather than caching it at init.
    // The modal (and the email links) may be injected via Ajax after this
    // method runs, so anything captured on page load would be stale or missing.
    const getModal = () => document.getElementById("members-email-modal");

    const populateMemberDetails = (
      modal,
      { name = "", position = "", extension = "", image = "" },
    ) => {
      const memberName = modal.querySelector(
        ".members-email-modal__member-name",
      );
      const memberPosition = modal.querySelector(
        ".members-email-modal__member-position",
      );
      const memberExtension = modal.querySelector(
        ".members-email-modal__member-extension",
      );
      const memberImageWrap = modal.querySelector(
        ".members-email-modal__member-image",
      );
      const memberImage = memberImageWrap?.querySelector("img");

      if (memberName) memberName.textContent = name;

      if (memberPosition) {
        memberPosition.textContent = position;
        memberPosition.hidden = !position;
      }

      if (memberExtension) {
        memberExtension.textContent = extension;
        memberExtension.hidden = !extension;
      }

      if (memberImage) {
        const showImageSkeleton = (isLoading) =>
          memberImageWrap.classList.toggle("is-loading", isLoading);

        if (image) {
          // The headshot is often a full-size upload, so it can lag behind the
          // rest of the modal. Show the blob skeleton until it has decoded.
          showImageSkeleton(true);
          memberImage.onload = () => showImageSkeleton(false);
          memberImage.onerror = () => showImageSkeleton(false);
          memberImage.src = image;
          memberImage.alt = name;
          memberImage.hidden = false;

          // Already cached: onload will not fire for a src that is unchanged.
          if (memberImage.complete && memberImage.naturalWidth) {
            showImageSkeleton(false);
          }
        } else {
          showImageSkeleton(false);
          memberImage.onload = null;
          memberImage.onerror = null;
          memberImage.removeAttribute("src");
          memberImage.alt = "";
          memberImage.hidden = true;
        }
      }
    };

    // Scoped to the modal on purpose. A page may also render form 3 inline
    // (the team member block), and getElementById would return that copy's
    // input instead of the modal's, sending the wrong member id.
    const populateMemberFields = (modal, { id = "" }) => {
      const idInput = modal.querySelector(`#input_${formId}_5`);
      if (idInput) idInput.value = id;
    };

    // Gravity Forms renders reCAPTCHA with grecaptcha.render( element.id ). If
    // the page already carries an inline copy of this form, the id in the
    // Ajax-loaded markup resolves back to the widget that is already rendered
    // and grecaptcha throws "reCAPTCHA has already been rendered in this
    // element" - from inside gform.recaptcha.renderOnRecaptchaLoaded's
    // setInterval, which then never clears and repeats forever. Give the
    // modal's container its own id so both widgets can render.
    const uniqueRecaptchaId = (formContainer) => {
      const captcha = formContainer.querySelector(".ginput_recaptcha");
      if (!captcha?.id) return;

      const duplicate = document.querySelector(
        `[id="${CSS.escape(captcha.id)}"]`,
      );
      if (!duplicate || duplicate === captcha) return;

      captcha.id = `${captcha.id}_modal`;
    };

    // innerHTML never executes <script> nodes, so Gravity Forms' own inline
    // init - the gform_ajax_frame handler that drives submission, and the
    // gform_post_render trigger that boots conditional logic and reCAPTCHA -
    // would never run. Re-inserting each script makes the browser execute it.
    // GF's init also defers the post-render trigger until the form is actually
    // visible, which is what the modal needs.
    const runFormScripts = (formContainer) => {
      formContainer.querySelectorAll("script").forEach((original) => {
        const script = document.createElement("script");

        Array.from(original.attributes).forEach(({ name, value }) => {
          script.setAttribute(name, value);
        });

        script.textContent = original.textContent;
        original.replaceWith(script);
      });
    };

    const needsFormReload = (formContainer) =>
      !formContainer.querySelector(`#gform_${formId}`) ||
      Boolean(
        formContainer.querySelector(`#gform_confirmation_wrapper_${formId}`),
      );

    // Never rejects: a failed reload must still leave the modal openable so the
    // member's details and the close button stay reachable.
    const reloadForm = async (formContainer) => {
      if (!window.oarcTheme?.ajaxUrl || !window.oarcTheme?.membersEmailNonce) {
        return;
      }

      try {
        const response = await fetch(window.oarcTheme.ajaxUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: new URLSearchParams({
            action: "oarc_members_email_form",
            nonce: window.oarcTheme.membersEmailNonce,
          }),
        });

        const payload = await response.json();
        if (!payload?.success || !payload.data?.html) return;

        formContainer.innerHTML = payload.data.html;
        uniqueRecaptchaId(formContainer);
        runFormScripts(formContainer);
      } catch (error) {
        console.error("Members email form could not be loaded.", error);
      }
    };

    const closeModal = () => {
      const modal = getModal();
      if (!modal) return;

      modal.classList.remove("active", "is-loading");
      modal.setAttribute("aria-hidden", "true");
      modal.setAttribute("aria-busy", "false");
      modal.hidden = true;
      document.body.classList.remove("members-email-modal-open");

      if (lastFocusedElement instanceof HTMLElement) {
        lastFocusedElement.focus();
      }
    };

    const openModal = async (memberData) => {
      const modal = getModal();
      if (!modal) return;

      lastFocusedElement = document.activeElement;

      const formContainer = modal.querySelector(".members-email-modal__form");
      const needsReload = Boolean(
        formContainer && needsFormReload(formContainer),
      );

      // Show the modal before fetching the form so a slow Ajax response is
      // covered by the skeleton instead of a click that appears to do nothing.
      populateMemberDetails(modal, memberData);
      populateMemberFields(modal, memberData);

      modal.classList.toggle("is-loading", needsReload);
      modal.setAttribute("aria-busy", needsReload ? "true" : "false");
      modal.hidden = false;
      modal.classList.add("active");
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("members-email-modal-open");
      modal.querySelector(".members-email-modal__close")?.focus();

      if (needsReload) {
        await reloadForm(formContainer);
        // The hidden member id input only exists once the form is in the DOM.
        populateMemberFields(modal, memberData);
        modal.classList.remove("is-loading");
        modal.setAttribute("aria-busy", "false");
      }

      // The user may have closed the modal while the form was loading.
      if (!modal.classList.contains("active")) return;

      modal.querySelector(`#input_${formId}_4`)?.focus();
    };

    // Delegated on document so email links and close triggers injected via
    // Ajax after page load continue to work without re-initialization.
    document.addEventListener("click", (event) => {
      const emailLink = event.target.closest("a[data-member-id]");
      if (emailLink) {
        event.preventDefault();

        openModal({
          name: emailLink.dataset.memberName || "",
          id: emailLink.dataset.memberId || "",
          position: emailLink.dataset.memberPosition || "",
          extension: emailLink.dataset.memberExtension || "",
          // Prefer ACF contact_image for the modal; fall back to featured image.
          image:
            emailLink.dataset.memberContactImage ||
            emailLink.dataset.memberImage ||
            "",
        });
        return;
      }

      if (event.target.closest("[data-members-email-modal-close]")) {
        closeModal();
      }
    });

    document.addEventListener("keydown", (event) => {
      const modal = getModal();
      if (event.key === "Escape" && modal?.classList.contains("active")) {
        closeModal();
      }
    });
  }

  initQuotesSlider() {
    if (typeof $.fn.slick === "undefined") return;

    $(".quotes-slider__slider").each(function initQuotesSliderInstance() {
      const $slider = $(this);
      if ($slider.hasClass("slick-initialized")) return;

      const autoplay = $slider.data("autoplay") === 1;
      const autoplaySpeed =
        parseInt($slider.data("autoplay-speed"), 10) || 4000;
      const fade = $slider.data("transition") === "fade";

      $slider.slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        dots: false,
        arrows: true,
        infinite: true,
        autoplay,
        autoplaySpeed,
        fade,
        responsive: [
          {
            breakpoint: 768,
            settings: {
              arrows: true,
              adaptiveHeight: true,
            },
          },
        ],
      });
    });
  }

  initResourcesBlock() {
    const containers = document.querySelectorAll(
      ".resources-block__archive.ajax-container",
    );
    if (!containers.length || typeof oarcTheme === "undefined") return;

    this.initSelect2();

    containers.forEach((container) => {
      const form = container.querySelector(".resources-block__form");
      if (!form) return;

      const results = container.querySelector(".results");
      const pagination = container.querySelector(".pagination");
      const emptyResults = container.querySelector(".empty-results");
      const searchInput = form.querySelector('[name="search"]');
      let searchTimeout = null;
      let activeRequest = null;

      const setLoading = (isLoading) => {
        container.classList.toggle("is-loading", isLoading);
      };

      // Keeps the browser URL in step with the block's state so a refresh or a
      // shared link lands on the same page/filters. The block markup opts in,
      // and PHP reads the same params when it renders the first paint.
      const syncUrl = container.hasAttribute("data-url-sync");
      const pageParam = container.dataset.pageParam || "resources_page";
      // Hidden fields that carry block configuration, not user choices.
      const configFields = [
        "post_type",
        "per_page",
        "pre_defined_category",
        "pre_defined_topic",
        "action",
        "nonce",
        "page",
      ];
      let isRestoring = false;

      // Visitor-facing fields only: anything named in configFields, or marked
      // data-config by the block (a hidden filter standing in for a block
      // setting), stays out of the URL.
      const stateFields = () =>
        Array.from(form.elements).filter(
          (element) =>
            element.name &&
            !configFields.includes(element.name) &&
            element.dataset.config === undefined,
        );

      // The block's own starting values, so a URL that omits a param restores
      // that param's default rather than blanking a select with no empty option
      // (the events block's event-time filter, for one).
      const defaults = new Map(
        stateFields().map((element) => [element.name, element.value || ""]),
      );

      const updateUrl = (page, replace) => {
        if (!syncUrl) return;

        const params = new URLSearchParams(window.location.search);

        stateFields().forEach((element) => {
          const value = (element.value || "").trim();
          if (value) {
            params.set(element.name, value);
          } else {
            params.delete(element.name);
          }
        });

        if (page > 1) {
          params.set(pageParam, String(page));
        } else {
          params.delete(pageParam);
        }

        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;

        if (replace) {
          window.history.replaceState({}, "", nextUrl);
        } else {
          window.history.pushState({}, "", nextUrl);
        }
      };

      // Pushes the URL's state back onto the form (browser back/forward).
      const restoreFromUrl = () => {
        const params = new URLSearchParams(window.location.search);

        isRestoring = true;
        stateFields().forEach((element) => {
          const value =
            params.get(element.name) ?? defaults.get(element.name) ?? "";
          if (element.value === value) return;

          element.value = value;
          if (element.tagName === "SELECT") {
            $(element).trigger("change");
          }
        });
        isRestoring = false;

        return Math.max(1, parseInt(params.get(pageParam), 10) || 1);
      };

      const updateResults = (data, page, history) => {
        if (!results || !pagination || !emptyResults) return;

        results.innerHTML = data.html || "";
        pagination.innerHTML = data.pagination || "";
        container.dataset.page = String(page);

        const hasResults = (data.found_posts || 0) > 0;
        results.style.display = hasResults ? "" : "none";
        emptyResults.style.display = hasResults ? "none" : "";
        container.classList.toggle(
          "has-more-results",
          (data.max_pages || 0) > 1,
        );

        if (history !== "none") {
          updateUrl(page, history === "replace");
        }
      };

      // history: "push" for pagination, "replace" for filter/search changes
      // (avoids a history entry per keystroke), "none" when replaying history.
      const fetchResources = (page = 1, history = "push") => {
        const formData = new FormData(form);
        formData.set(
          "action",
          container.dataset.action || "oarc_resources_block",
        );
        formData.set("page", String(page));
        formData.set("nonce", oarcTheme.nonce);

        if (activeRequest) {
          activeRequest.abort();
        }

        activeRequest = new AbortController();
        setLoading(true);

        fetch(oarcTheme.ajaxUrl, {
          method: "POST",
          body: formData,
          signal: activeRequest.signal,
        })
          .then((response) => response.json())
          .then((response) => {
            if (!response.success) return;
            updateResults(response.data, page, history);
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              throw error;
            }
          })
          .finally(() => {
            setLoading(false);
            activeRequest = null;
          });
      };

      $(form)
        .find("select")
        .on("change.select2", () => {
          if (isRestoring) return;
          fetchResources(1, "replace");
        });

      if (searchInput) {
        searchInput.addEventListener("input", () => {
          clearTimeout(searchTimeout);
          searchTimeout = setTimeout(() => fetchResources(1, "replace"), 300);
        });

        searchInput.addEventListener("keydown", (event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            clearTimeout(searchTimeout);
            fetchResources(1, "replace");
          }
        });
      }

      container.addEventListener("click", (event) => {
        const pageLink = event.target.closest(".pagination a[data-page]");
        if (!pageLink || !container.contains(pageLink)) return;

        event.preventDefault();
        const page = parseInt(pageLink.dataset.page, 10);
        if (!Number.isNaN(page)) {
          fetchResources(page, "push");
        }
      });

      if (syncUrl) {
        window.addEventListener("popstate", () => {
          clearTimeout(searchTimeout);
          fetchResources(restoreFromUrl(), "none");
        });
      }
    });
  }

  initSearchPage() {
    const containers = document.querySelectorAll(
      ".search-page.ajax-container",
    );
    if (!containers.length || typeof oarcTheme === "undefined") return;

    this.initSelect2();

    containers.forEach((container) => {
      const form = container.querySelector(".search-page__form");
      if (!form) return;

      const results = container.querySelector(".results");
      const pagination = container.querySelector(".pagination");
      const emptyResults = container.querySelector(".empty-results");
      let activeRequest = null;

      const setLoading = (isLoading) => {
        container.classList.toggle("is-loading", isLoading);
      };

      const updateUrl = (page) => {
        const params = new URLSearchParams(window.location.search);
        const searchValue = form.querySelector('[name="s"]')?.value || "";
        const filterValue =
          form.querySelector('[name="post_type_filter"]')?.value || "";

        if (searchValue) {
          params.set("s", searchValue);
        } else {
          params.delete("s");
        }

        if (filterValue) {
          params.set("post_type_filter", filterValue);
        } else {
          params.delete("post_type_filter");
        }

        if (page > 1) {
          params.set("paged", String(page));
        } else {
          params.delete("paged");
        }

        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}`;
        window.history.replaceState({}, "", nextUrl);
      };

      const updateResults = (data, page) => {
        if (!results || !pagination || !emptyResults) return;

        results.innerHTML = data.html || "";
        pagination.innerHTML = data.pagination || "";
        container.dataset.page = String(page);

        const hasResults = (data.found_posts || 0) > 0;
        results.style.display = hasResults ? "" : "none";
        emptyResults.style.display = hasResults ? "none" : "";
        container.classList.toggle(
          "has-more-results",
          (data.max_pages || 0) > 1,
        );

        updateUrl(page);
      };

      const fetchSearchResults = (page = 1) => {
        const formData = new FormData(form);
        formData.set(
          "action",
          container.dataset.action || "oarc_search_page",
        );
        formData.set("page", String(page));
        formData.set("nonce", oarcTheme.searchNonce || oarcTheme.nonce);

        if (activeRequest) {
          activeRequest.abort();
        }

        activeRequest = new AbortController();
        setLoading(true);

        fetch(oarcTheme.ajaxUrl, {
          method: "POST",
          body: formData,
          signal: activeRequest.signal,
        })
          .then((response) => response.json())
          .then((response) => {
            if (!response.success) return;
            updateResults(response.data, page);
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              throw error;
            }
          })
          .finally(() => {
            setLoading(false);
            activeRequest = null;
          });
      };

      $(form)
        .find("select")
        .on("change.select2", () => fetchSearchResults(1));

      form.addEventListener("submit", (event) => {
        event.preventDefault();
        fetchSearchResults(1);
      });

      container.addEventListener("click", (event) => {
        const pageLink = event.target.closest(".pagination a[data-page]");
        if (!pageLink || !container.contains(pageLink)) return;

        event.preventDefault();
        const page = parseInt(pageLink.dataset.page, 10);
        if (!Number.isNaN(page)) {
          fetchSearchResults(page);
        }
      });
    });
  }
}

/* eslint-disable no-new */
new Theme();

(() => {
	'use strict';

	const fieldSelector = '.leadership_field';
	const groupSelector = '.oarc-list-radio-group';
	const radioSelector = 'input.oarc-list-radio';
	const hiddenSelector = 'input.oarc-list-radio-value';

	let observer = null;

	/**
	 * Assign a unique radio name to every Yes/No group.
	 *
	 * This supports:
	 * - Multiple leadership fields on one page.
	 * - Multiple Gravity Forms on one page.
	 * - Dynamically added List rows.
	 * - Forms rendered through AJAX.
	 */
	const initializeRadioGroups = () => {
		const fields = document.querySelectorAll(fieldSelector);

		fields.forEach((field, fieldIndex) => {
			const groups = field.querySelectorAll(groupSelector);

			groups.forEach((group, groupIndex) => {
				const fieldIdentifier =
					field.id ||
					`leadership_field_${fieldIndex}`;

				const radioName =
					`${fieldIdentifier}_radio_group_${groupIndex}`;

				const radios = group.querySelectorAll(radioSelector);

				radios.forEach((radio, radioIndex) => {
					radio.name = radioName;

					/*
					 * Gravity Forms clones List rows, which may also clone IDs.
					 * Generate a unique ID so each label targets the correct radio.
					 */
					const radioValue = radio.value
						.toLowerCase()
						.replace(/[^a-z0-9_-]/g, '_');

					const radioId =
						`${radioName}_${radioValue}_${radioIndex}`;

					radio.id = radioId;

					const option = radio.closest(
						'.oarc-list-radio-option'
					);

					const label = option?.querySelector('label');

					if (label) {
						label.htmlFor = radioId;
					}
				});

				/*
				 * Restore the checked radio from the hidden Gravity Forms value.
				 */
				const hiddenInput = group.querySelector(hiddenSelector);

				if (hiddenInput instanceof HTMLInputElement) {
					radios.forEach((radio) => {
						radio.checked =
							radio.value === hiddenInput.value;
					});
				}
			});
		});
	};

	/**
	 * Synchronize the selected radio value with the hidden input
	 * that Gravity Forms submits and saves.
	 */
	const handleRadioChange = (event) => {
		const radio = event.target;

		if (
			!(radio instanceof HTMLInputElement) ||
			!radio.matches(radioSelector)
		) {
			return;
		}

		const field = radio.closest(fieldSelector);

		if (!field) {
			return;
		}

		const group = radio.closest(groupSelector);

		if (!group) {
			return;
		}

		const hiddenInput = group.querySelector(hiddenSelector);

		if (!(hiddenInput instanceof HTMLInputElement)) {
			return;
		}

		hiddenInput.value = radio.value;

		/*
		 * Trigger input/change events in case Gravity Forms or another
		 * script is listening for value changes.
		 */
		hiddenInput.dispatchEvent(
			new Event('input', {
				bubbles: true,
			})
		);

		hiddenInput.dispatchEvent(
			new Event('change', {
				bubbles: true,
			})
		);
	};

	/**
	 * Observe the page for dynamically added or removed Gravity Forms rows.
	 */
	const startObserver = () => {
		if (observer) {
			observer.disconnect();
		}

		observer = new MutationObserver((mutations) => {
			const relevantChange = mutations.some((mutation) => {
				if (mutation.type !== 'childList') {
					return false;
				}

				return (
					mutation.addedNodes.length > 0 ||
					mutation.removedNodes.length > 0
				);
			});

			if (relevantChange) {
				initializeRadioGroups();
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	};

	/**
	 * Initialize all matching List fields.
	 */
	const initialize = () => {
		initializeRadioGroups();
		startObserver();
	};

	document.addEventListener('change', handleRadioChange);

	if (document.readyState === 'loading') {
		document.addEventListener(
			'DOMContentLoaded',
			initialize
		);
	} else {
		initialize();
	}

	/**
	 * Gravity Forms AJAX and multi-page rendering.
	 */
	document.addEventListener(
		'gform/postRender',
		initialize
	);
})();